import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Avatar, TextField, Typography, Chip, Box, IconButton, Pagination, InputAdornment, Tooltip
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import EditIcon from "@mui/icons-material/Edit"; 
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { getEstudiantes, updateEstadoUsuarioEstudiante, recoverPassword as recoverPasswordApi } from "../services";
import PasswordIcon from "@mui/icons-material/Password"; 
import UpdateUserModal from "./UpdateUserModal";

const rowsPerPage = 5;

export default function UserTable() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
  };

  const fetchUsers = async () => {
    try {
      const data = await getEstudiantes();
      setUsers(data.data);
    } catch (error) {
      console.error("Error al cargar usuarios", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChangeStatus = async (userId, currentStatus) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "¿Quieres cambiar el estado de este usuario?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const newStatus = getNextStatus(currentStatus);

        await updateEstadoUsuarioEstudiante(userId, newStatus);

        await fetchUsers(); // <-- Se vuelve a consultar la lista actualizada

        Swal.fire("¡Actualizado!", "El estado del usuario ha sido cambiado.", "success");
      } catch (error) {
        Swal.fire("Error", "No se pudo cambiar el estado.", "error");
      }
    }
  };

  const recoverPassword = async (correo) => {
    if (!correo) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se puede recuperar contraseña sin un correo.",
      });
      return;
    }
  
    try {
      const response = await recoverPasswordApi(correo);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Correo enviado",
          text: "Se ha enviado un correo con instrucciones para recuperar la contraseña.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo enviar el correo de recuperación.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: error.message || "No se pudo conectar con el servidor.",
      });
    }
  }

  const getNextStatus = (current) => {
    return current === "1" ? "0" : "1";
  };

  const filteredUsers = users.filter((user) => {
    const searchText = search.toLowerCase();
    return (
      user.PrimerNombre.toLowerCase().includes(searchText) ||
      user.Correo.toLowerCase().includes(searchText) ||
      user.Documento.toLowerCase().includes(searchText)
    );
  });

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <GroupIcon color="primary" />
        <Typography variant="h6">Gestión de usuarios</Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          size="small"
          placeholder="Buscar estudiantes"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Tipo Documento</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={
                        user.FotoPerfil?.startsWith("data:image")
                          ? user.FotoPerfil
                          : `data:image/jpeg;base64,${user.FotoPerfil}`
                      }
                    />
                    <Box>
                      <Typography>{user.PrimerNombre} {user.PrimerApellido}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.Correo}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.TipoDoc}</TableCell>
                <TableCell>{user.Documento}</TableCell>
                <TableCell>
                  <Chip
                    label={user.Estado === "1" ? "Activo" : "Inactivo"}
                    color={user.Estado === "1" ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                <Tooltip title="Activar / Desactivar">
                  <IconButton
                    color="primary"
                    onClick={() => handleChangeStatus(user.idUsuario, user.Estado)}
                  >
                    <FlagIcon fontSize="small" />
                  </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                  <IconButton
                  color="primary"
                  onClick={() => handleEditClick(user)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                </Tooltip>
                <Tooltip title="Recuperar contraseña">
                <IconButton
                onClick={() => recoverPassword(user.Correo)}
                color="primary"
              >
                <PasswordIcon />
              </IconButton>
              </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(filteredUsers.length / rowsPerPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>
      <UpdateUserModal
        open={openModal}
        onClose={handleCloseModal}
        userData={selectedUser}
        onUserUpdated={fetchUsers}
      />
    </Box>
  );
}
